import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPut } from "@/shared/api";
import { useCurrentBoardSlug } from "@/shared/hooks/useCurrentBoardSlug";
import { toast } from "@/shared/ui/toast";

interface SoudaneParams {
  threadId: string;
  // ThreadDetailPost.postNumber は server schema 上 string なので string で受ける。
  // 新API path param は number 型だが URL 文字列化で問題なし。
  postNumber: string;
}

// 新API: PUT /reactions/{slug}/{threadId}/{postNumber}?actionType=soudane
// request body なし、actionType をクエリパラメータで指定 (spec §8 / Task 1.5)
export function useSoudaneMutation(): UseMutationResult<
  undefined,
  unknown,
  SoudaneParams
> {
  const queryClient = useQueryClient();
  const slug = useCurrentBoardSlug();

  return useMutation({
    mutationFn: async ({
      threadId,
      postNumber,
    }: SoudaneParams): Promise<undefined> => {
      // request body なし。OpenAPI strict validation を避けるため {} も渡さない
      // 204 / 空 body は client.ts の unwrap が undefined を返す
      await apiPut<void>(
        `/reactions/${slug}/${threadId}/${postNumber}?actionType=soudane`,
      );
      return undefined;
    },
    onSuccess: (_data: undefined, variables: SoudaneParams) => {
      const { threadId } = variables;
      queryClient.invalidateQueries({ queryKey: ["thread", slug, threadId] });
      // Task 8b 用: reactions の集計取得 invalidate
      queryClient.invalidateQueries({
        queryKey: ["reactions", slug, threadId],
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : undefined;
      if (message === "そうだね済みです") {
        toast.success(`そうだね済みです`);
      } else if (message) {
        toast.error(`エラー: ${message}`);
      } else {
        toast.error("そうだねの送信に失敗しました");
      }
    },
  });
}
